import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Shirt, PlusCircle, FilterIcon, Package } from 'lucide-react';
import AppliedFilters from '@/components/AppliedFilters';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import DataTable from '@/components/DataTable';

import DynamicFormModal from '@/components/DynamicFormModal';
import FilterDrawer from '@/components/FilterDrawer';
import ExportExcelButton from '@/components/ExportExcelButton';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { garmentTypesColumns, columnsExcel } from './utils/garmentTypesColumns';
import { createGarmentType, getGarmentTypes, updateGarmentType, deleteGarmentType, exportGarmentTypes } from './Services/garmentTypes.services';
import AddToInventoryModal from './components/AddToInventoryModal';

const GarmentTypesPage = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ query: {}, page: 1, limit: 10 });
  const [totalRecords, setTotalRecords] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [isAddToInventoryOpen, setIsAddToInventoryOpen] = useState(false);
  const [selectedGarmentType, setSelectedGarmentType] = useState(null);
  const [edificios, setEdificios] = useState([]);
  const [isLoadingEdificios, setIsLoadingEdificios] = useState(false);
  const { toast } = useToast();

  // Función para cargar portafolios
  const loadEdificios = useCallback(async () => {
    setIsLoadingEdificios(true);
    try {
      const { fetchEdificios } = await import('@/pages/parametrizacion/edificios/Services/edificios.services');
      const response = await fetchEdificios({ 
        limit: 500, 
        offset: 1, 
        query: { estado: 'Activo' } 
      });

      if (response && response.data) {
        const edificiosOptions = response.data.map(edificio => ({
          value: edificio._id,
          label: edificio.nombre
        }));
        setEdificios(edificiosOptions);
      }
    } catch (error) {
      console.error('Error al cargar portafolios:', error);
      toast({
        title: "Error",
        description: "No se pudieron cargar los portafolios. Por favor, recarga la página.",
        variant: "destructive",
      });
    } finally {
      setIsLoadingEdificios(false);
    }
  }, [toast]);

  // Transformar filtros para buscar en campos anidados
  const transformFiltersForQuery = useCallback((query) => {
    const transformedQuery = { ...query };
    
    // Si hay filtro de edificio, buscar por edificio.id
    if (transformedQuery.edificio) {
      transformedQuery['edificio.id'] = transformedQuery.edificio;
      delete transformedQuery.edificio;
    }
    
    return transformedQuery;
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      // Transformar filtros antes de enviar
      const transformedQuery = transformFiltersForQuery(filters.query);
      const response = await getGarmentTypes({ ...filters, query: transformedQuery });
      
      // Asegurar que siempre sea un array
      setData(Array.isArray(response.data) ? response.data : []);
      setTotalRecords(response.totalRecords || 0);
    } catch (error) {
      console.error('Error al cargar tipos de prenda:', error);
      setData([]);
      setTotalRecords(0);
      toast({
        title: 'Error',
        description: 'No se pudieron cargar los tipos de prenda.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [filters, toast, transformFiltersForQuery]);

  useEffect(() => {
    loadEdificios();
  }, [loadEdificios]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleFilter = (newFilters) => {
    setFilters((prev) => ({ ...prev, query: newFilters, page: 1 }));
  };

  const handleClearFilters = () => {
    setFilters((prev) => ({ ...prev, query: {}, page: 1 }));
  };

  const handlePageChange = (newPage) => {
    setFilters((prev) => ({ ...prev, page: newPage }));
  };

  const handleLimitChange = (newLimit) => {
    setFilters((prev) => ({ ...prev, limit: newLimit, page: 1 }));
  };

  const handleSave = async (formData) => {
    try {
      if (editingItem) {
        await updateGarmentType(editingItem._id, formData);
        toast({ title: 'Éxito', description: 'Tipo de prenda actualizado correctamente.' });
      } else {
        await createGarmentType(formData);
        toast({ title: 'Éxito', description: 'Tipo de prenda creado correctamente.' });
      }
      fetchData();
      setIsModalOpen(false);
      setEditingItem(null);
    } catch (error) {
      toast({
        title: 'Error',
        description: `No se pudo guardar el tipo de prenda. ${error.message}`,
        variant: 'destructive',
      });
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setIsModalOpen(true);
  };

  const handleDelete = (item) => {
    setItemToDelete(item);
    setIsDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (itemToDelete) {
      try {
        await deleteGarmentType(itemToDelete._id);
        toast({ title: 'Éxito', description: 'Tipo de prenda eliminado correctamente.' });
        fetchData();
      } catch (error) {
        toast({
          title: 'Error',
          description: 'No se pudo eliminar el tipo de prenda.',
          variant: 'destructive',
        });
      }
    }
    setIsDeleteDialogOpen(false);
    setItemToDelete(null);
  };

  const handleAddToInventory = (garmentType) => {
    setSelectedGarmentType(garmentType);
    setIsAddToInventoryOpen(true);
  };

  const handleInventorySuccess = (message, type = 'success') => {
    toast({
      title: type === 'success' ? 'Éxito' : 'Error',
      description: message,
      variant: type === 'error' ? 'destructive' : 'default',
    });
  };

  const handleAction = (action, item) => {
    if (action === 'edit') {
      handleEdit(item);
    } else if (action === 'delete') {
      handleDelete(item);
    } else if (action === 'addToInventory') {
      handleAddToInventory(item);
    }
  };

  // Agregar acción personalizada para agregar al inventario
  const customActions = [
    {
      key: 'addToInventory',
      icon: Package,
      tooltip: 'Agregar al Inventario',
      className: 'hover:bg-blue-100 hover:text-blue-700 dark:hover:bg-blue-900 dark:hover:text-blue-300'
    }
  ];

  const tableColumns = garmentTypesColumns;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.5 }}

    >
      <Card>
        <CardHeader className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="bg-primary/10 p-3 rounded-lg">
              <Shirt className="h-8 w-8 text-primary" />
            </div>
            <div>
              <CardTitle className="text-2xl">Parametrización de Tipos de Prenda</CardTitle>
              <CardDescription>Gestiona los tipos de prenda del sistema.</CardDescription>
            </div>
          </div>
          <div className="flex gap-2 mt-4 md:mt-0">
            <Button onClick={() => setIsFilterOpen(true)} variant="outline">
              <FilterIcon className="h-4 w-4 mr-2" />
              Filtrar
            </Button>
            <ExportExcelButton
              service={(params) => exportGarmentTypes(params.query)}
              filters={filters.query}
              columns={[
                { key: 'nombre', header: 'Nombre' },
                { key: 'peso', header: 'Peso (g)' },
                { key: 'descripcion', header: 'Descripción' },
                { key: 'estado', header: 'Estado' },
              ]}
              fileName="Tipos_de_Prenda"
            />
            <Button onClick={() => {
              setEditingItem(null);
              setIsModalOpen(true);
            }}>
              <PlusCircle className="h-4 w-4 mr-2" />
              Crear Nuevo
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <AppliedFilters 
            filters={filters.query} 
            onRemoveFilter={(key) => {
              const newQuery = { ...filters.query };
              delete newQuery[key];
              setFilters(prev => ({ ...prev, query: newQuery, page: 1 }));
            }}
            fields={[
              { id: 'nombre', label: 'Nombre', type: 'text' },
              { id: 'estado', label: 'Estado', type: 'select', options: [
                { value: 'Activo', label: 'Activo' },
                { value: 'Inactivo', label: 'Inactivo' },
              ]},
              { 
                id: 'edificio', 
                label: 'Portafolio', 
                type: 'select',
                options: edificios
              },
            ]}
          />
          <DataTable
            data={data}
            columns={tableColumns}
            isLoading={loading}
            onAction={handleAction}
            page={filters.page}
            limit={filters.limit}
            totalRecords={totalRecords}
            onPageChange={handlePageChange}
            onLimitChange={handleLimitChange}
            columnsConfig={garmentTypesColumns}
            customActions={customActions}
          />
        </CardContent>
      </Card>

      <AnimatePresence>
        {isModalOpen && (
          <DynamicFormModal
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            onSave={handleSave}
            fields={garmentTypesColumns}
            item={editingItem}
            title={editingItem ? 'Editar Tipo de Prenda' : 'Crear Tipo de Prenda'}
          />
        )}
      </AnimatePresence>

      <FilterDrawer
        isOpen={isFilterOpen}
        onClose={() => setIsFilterOpen(false)}
        onApplyFilters={handleFilter}
        fields={[
          { id: 'nombre', label: 'Nombre', type: 'text' },
          { id: 'estado', label: 'Estado', type: 'select', options: [
            { value: 'Activo', label: 'Activo' },
            { value: 'Inactivo', label: 'Inactivo' },
          ]},
          { 
            id: 'edificio', 
            label: 'Portafolio', 
            type: 'select',
            options: edificios
          },
        ]}
        initialFilters={filters.query}
      />

      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Estás seguro?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. Esto eliminará permanentemente el tipo de prenda.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete}>Confirmar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AddToInventoryModal
        isOpen={isAddToInventoryOpen}
        onClose={() => setIsAddToInventoryOpen(false)}
        garmentType={selectedGarmentType}
        onSuccess={handleInventorySuccess}
      />
    </motion.div>
  );
};

export default GarmentTypesPage;
